import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Activity, BarChart3, DollarSign, Layers } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_digital-asset-io/artifacts/w5ymcp91_IODX%20logo%20with%20blue%20gradient%20background.png';

// Named constants (replacing magic numbers)
const BASE_PRICE = 2.847;
const BASE_MARKET_CAP = 284_700_000;
const BASE_VOLUME = 18_420_000;
const MIN_PRICE = 2.40;
const MIN_CHANGE = -2;
const MIN_VOLUME = 10_000_000;
const PRICE_DRIFT = 0.012;
const CHANGE_DRIFT = 0.15;
const MCAP_DRIFT = 500_000;
const VOLUME_DRIFT = 200_000;
const SPARK_DRIFT = 0.3;
const SPARKLINE_COUNT = 24;
const UPDATE_INTERVAL_MS = 3000;
const FLASH_DURATION_MS = 600;
const BILLION = 1_000_000_000;
const MILLION = 1_000_000;

const tickerMotion = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.3, duration: 0.6 },
};

function generateSparkline() {
  const points = [];
  let val = BASE_PRICE;
  for (let i = 0; i < SPARKLINE_COUNT; i++) {
    val += (Math.random() - 0.48) * 0.05;
    points.push(Math.max(val, MIN_PRICE));
  }
  return points;
}

function formatNum(n) {
  if (n >= BILLION) return `$${(n / BILLION).toFixed(2)}B`;
  if (n >= MILLION) return `$${(n / MILLION).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

function getFlashColor(flash) {
  if (flash === 'up') return 'text-green-400';
  if (flash === 'down') return 'text-red-400';
  return 'text-[#F1F5F9]';
}

function MiniChart({ data, positive }) {
  const width = 120;
  const height = 36;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  const fillPoints = `0,${height} ${points} ${width},${height}`;
  const gradientId = positive ? 'sparkGreen' : 'sparkRed';
  const color = positive ? '#22c55e' : '#ef4444';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill={`url(#${gradientId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function useTickerData() {
  const [price, setPrice] = useState(BASE_PRICE);
  const [change24h, setChange24h] = useState(5.42);
  const [marketCap, setMarketCap] = useState(BASE_MARKET_CAP);
  const [volume, setVolume] = useState(BASE_VOLUME);
  const [sparkData, setSparkData] = useState(generateSparkline);
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);

  const tick = useCallback(() => {
    setPrice(prev => {
      const delta = (Math.random() - 0.47) * PRICE_DRIFT;
      const newPrice = Math.max(prev + delta, MIN_PRICE);
      setFlash(newPrice > prev ? 'up' : 'down');
      return newPrice;
    });

    setChange24h(prev => Math.max(prev + (Math.random() - 0.48) * CHANGE_DRIFT, MIN_CHANGE));
    setMarketCap(prev => prev + (Math.random() - 0.47) * MCAP_DRIFT);
    setVolume(prev => Math.max(prev + (Math.random() - 0.5) * VOLUME_DRIFT, MIN_VOLUME));
    setSparkData(prev => [...prev.slice(1), BASE_PRICE + (Math.random() - 0.47) * SPARK_DRIFT]);

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), FLASH_DURATION_MS);
  }, []);

  useEffect(() => {
    const interval = setInterval(tick, UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [tick]);

  return { price, change24h, marketCap, volume, sparkData, flash };
}

function TickerLabel() {
  return (
    <div className="flex items-center gap-2 shrink-0 pr-4 border-r border-white/10">
      <img src={LOGO_URL} alt="IODX" className="h-6 w-6 rounded-sm object-cover" />
      <span className="text-sm font-bold text-[#F1F5F9] tracking-wide">IODX</span>
      <span className="text-xs text-[#94A3B8]">/ USD</span>
    </div>
  );
}

function TickerPrice({ price, flash }) {
  return (
    <div className="flex items-center gap-2 shrink-0 pr-4 border-r border-white/10" data-testid="ticker-price">
      <DollarSign className="w-3.5 h-3.5 text-[#60A5FA]" strokeWidth={1.5} />
      <span className={`text-sm font-bold tabular-nums transition-colors duration-500 ${getFlashColor(flash)}`}>
        {price.toFixed(3)}
      </span>
    </div>
  );
}

function TickerChange({ change24h }) {
  const isPositive = change24h >= 0;
  return (
    <div className="flex items-center gap-1.5 shrink-0 pr-4 border-r border-white/10" data-testid="ticker-change">
      {isPositive ? (
        <TrendingUp className="w-3.5 h-3.5 text-green-400" strokeWidth={1.5} />
      ) : (
        <TrendingDown className="w-3.5 h-3.5 text-red-400" strokeWidth={1.5} />
      )}
      <span className={`text-xs font-semibold tabular-nums ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        {isPositive ? '+' : ''}{change24h.toFixed(2)}%
      </span>
      <span className="text-xs text-[#94A3B8]">24h</span>
    </div>
  );
}

function LiveIndicator() {
  return (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="ticker-live">
      <Activity className="w-3.5 h-3.5 text-green-400" strokeWidth={1.5} />
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
      </span>
      <span className="text-xs text-green-400 font-medium">LIVE</span>
    </div>
  );
}

export default function TokenTicker() {
  const { price, change24h, marketCap, volume, sparkData, flash } = useTickerData();
  const isPositive = change24h >= 0;
  const formattedMcap = useMemo(() => formatNum(marketCap), [marketCap]);
  const formattedVolume = useMemo(() => formatNum(volume), [volume]);

  return (
    <motion.div
      {...tickerMotion}
      data-testid="token-ticker"
      className="border-b border-[rgba(59,130,246,0.1)] bg-[#060B1E]/90 backdrop-blur-md"
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex items-center gap-4 py-2.5 overflow-x-auto scrollbar-hide">
          <TickerLabel />
          <TickerPrice price={price} flash={flash} />
          <TickerChange change24h={change24h} />

          <div className="shrink-0 pr-4 border-r border-white/10" data-testid="ticker-chart">
            <MiniChart data={sparkData} positive={isPositive} />
          </div>

          <div className="flex items-center gap-1.5 shrink-0 pr-4 border-r border-white/10" data-testid="ticker-mcap">
            <Layers className="w-3.5 h-3.5 text-[#60A5FA]" strokeWidth={1.5} />
            <span className="text-xs text-[#94A3B8]">MCap</span>
            <span className="text-xs font-medium text-[#F1F5F9] tabular-nums">{formattedMcap}</span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0 pr-4 border-r border-white/10" data-testid="ticker-volume">
            <BarChart3 className="w-3.5 h-3.5 text-[#60A5FA]" strokeWidth={1.5} />
            <span className="text-xs text-[#94A3B8]">Vol</span>
            <span className="text-xs font-medium text-[#F1F5F9] tabular-nums">{formattedVolume}</span>
          </div>

          <LiveIndicator />
        </div>
      </div>
    </motion.div>
  );
}
